<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    @input="$emit('input', $event)"
  >
    <v-card class="resume-dialog" tile>
      <v-toolbar dark color="#263238" dense flat>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          <v-icon left>mdi-file-account</v-icon>เอกสารแสดงประวัติ
        </v-toolbar-title>
        <v-spacer />
        <v-btn text dark :disabled="loading" @click="printResume">
          <v-icon left>mdi-printer</v-icon>พิมพ์
        </v-btn>
        <v-btn text dark :disabled="loading" @click="exportWord">
          <v-icon left>mdi-file-word</v-icon>ดาวน์โหลด (Word)
        </v-btn>
      </v-toolbar>

      <div class="resume-viewport">
        <div v-if="loading" class="text-center py-16">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-4 grey--text">กำลังโหลดข้อมูลประวัติ...</div>
        </div>

        <!-- เนื้อหาใน #resume-print-root ถูกคัดลอกไปพิมพ์ (iframe) และส่งออก Word
             จึงใช้ HTML table ธรรมดา + class ใน RESUME_CSS เท่านั้น ห้ามใช้ component Vuetify ข้างใน -->
        <div v-else ref="printRoot" id="resume-print-root">
          <div class="rs-page">
            <!-- ส่วนหัว -->
            <table class="rs-header">
              <tr>
                <td class="rs-header-text">
                  <div class="rs-title">เอกสารแสดงประวัติ</div>
                  <div class="rs-subtitle">{{ userTypeLabel }}</div>
                  <div class="rs-name">{{ fullName }}</div>
                  <div>ตำแหน่ง {{ dash(users.position_name) }}
                    <span v-if="users.rang_name"> วิทยฐานะ {{ users.rang_name }}</span>
                  </div>
                  <div>{{ dash(users.college_name) }}</div>
                  <div class="rs-muted">ข้อมูล ณ วันที่ {{ thaiDate(today) }}</div>
                </td>
                <td class="rs-photo-cell">
                  <img class="rs-photo" :src="photoUrl" alt="" />
                </td>
              </tr>
            </table>

            <!-- 1. ข้อมูลพื้นฐาน -->
            <div class="rs-section">1. ข้อมูลพื้นฐาน</div>
            <table class="rs-kv">
              <tr>
                <th>ชื่อ-นามสกุล</th><td>{{ fullName }}</td>
                <th>เลขประจำตัวประชาชน</th><td>{{ formatIdCard(users.id_card) }}</td>
              </tr>
              <tr>
                <th>เพศ</th><td>{{ dash(users.sex) }}</td>
                <th>ศาสนา</th><td>{{ dash(users.religion) }}</td>
              </tr>
              <tr>
                <th>วันเดือนปีเกิด</th><td>{{ birthDateText }}</td>
                <th>อายุ</th><td>{{ ageText }}</td>
              </tr>
              <tr>
                <th>สถานภาพสมรส</th><td>{{ maritalLabel }}</td>
                <th>ปีเกษียณอายุราชการ</th><td>{{ dash(users.retrire_year) }}</td>
              </tr>
            </table>

            <!-- 2. ข้อมูลการรับราชการปัจจุบัน -->
            <div class="rs-section">2. ข้อมูลตำแหน่งและการรับราชการปัจจุบัน</div>
            <table class="rs-kv">
              <tr>
                <th>ประเภทบุคลากร</th><td>{{ userTypeLabel }}</td>
                <th>ตำแหน่ง</th><td>{{ dash(users.position_name) }}</td>
              </tr>
              <tr>
                <th>เลขที่ตำแหน่ง</th><td>{{ dash(users.id_position) }}</td>
                <th>เงินเดือน</th><td>{{ salaryText }}</td>
              </tr>
              <tr>
                <th>วิทยฐานะ</th><td>{{ dash(users.rang_name) }}</td>
                <th>วันที่ได้รับวิทยฐานะ</th><td>{{ thaiDate(users.date_rang_level) }}</td>
              </tr>
              <tr>
                <th>สถานศึกษาปัจจุบัน</th><td>{{ dash(users.college_name) }}</td>
                <th>สาขาวิชาที่ปฏิบัติการสอน</th><td>{{ dash(users.branch_name_th) }}</td>
              </tr>
              <tr>
                <th>วันที่บรรจุ</th><td>{{ appointDateText }}</td>
                <th>อายุราชการ</th><td>{{ serviceAgeText }}</td>
              </tr>
              <tr>
                <th>คำสั่งมาปฏิบัติงาน ณ สถานศึกษาปัจจุบัน</th><td>{{ dash(users.order_app_now) }}</td>
                <th>วันที่ปฏิบัติงาน ณ สถานศึกษาปัจจุบัน</th>
                <td>{{ thaiDate(users.date_app_now) }}
                  <span v-if="users.date_app_now">({{ durationText(users.date_app_now) }})</span>
                </td>
              </tr>
              <tr>
                <th>วันที่ดำรงตำแหน่งปัจจุบัน</th><td>{{ thaiDate(users.date_app_positonnow) }}</td>
                <th>สถานะช่วยราชการ</th>
                <td>{{ String(users.govenment_status) === "1" ? "ช่วยราชการ" : "ไม่ได้ช่วยราชการ" }}</td>
              </tr>
              <tr v-if="isGovEm">
                <th>สัญญาจ้างล่าสุด</th>
                <td>{{ thaiDate(users.last_contract_start) }} - {{ thaiDate(users.last_contract_end) }}</td>
                <th>เลขที่สัญญาจ้าง</th><td>{{ dash(users.govem_contract) }}</td>
              </tr>
            </table>

            <!-- 3. ที่อยู่ -->
            <div class="rs-section">3. ที่อยู่ปัจจุบันและการติดต่อ</div>
            <table class="rs-kv">
              <tr>
                <th>ที่อยู่</th><td colspan="3">{{ addressText }}</td>
              </tr>
              <tr>
                <th>โทรศัพท์มือถือ</th><td>{{ dash(users.tel_p) }}</td>
                <th>โทรศัพท์บ้าน</th><td>{{ dash(users.pt_hometel) }}</td>
              </tr>
              <tr>
                <th>E-mail</th><td>{{ dash(users.e_mail) }}</td>
                <th>โทรสาร</th><td>{{ dash(users.pt_fax) }}</td>
              </tr>
            </table>

            <!-- 4. คู่สมรส -->
            <template v-if="hasSpouse">
              <div class="rs-section">4. ข้อมูลคู่สมรส</div>
              <table class="rs-kv">
                <tr>
                  <th>ชื่อ-นามสกุล</th>
                  <td>{{ users.title_sm }}{{ users.frist_namem }} {{ users.last_namem }}</td>
                  <th>อาชีพ</th><td>{{ dash(users.occupation_sm) }}</td>
                </tr>
                <tr>
                  <th>สถานที่ทำงาน</th><td>{{ dash(users.work_placem) }}</td>
                  <th>จังหวัด</th><td>{{ dash(users.spouse_province_name) }}</td>
                </tr>
              </table>
            </template>

            <!-- 5. การศึกษา -->
            <div class="rs-section">{{ n("education") }}. วุฒิการศึกษา</div>
            <table class="rs-kv">
              <tr>
                <th>วุฒิการศึกษา (ที่ใช้บรรจุ/สูงสุด)</th><td>{{ dash(users.ed_abb) }}</td>
                <th>สาขาวิชา</th><td>{{ dash(users.ed_name) }}</td>
              </tr>
              <tr v-if="users.ed_university || users.country_graduation">
                <th>สถาบันการศึกษา</th><td>{{ dash(users.ed_university) }}</td>
                <th>ประเทศที่สำเร็จการศึกษา</th><td>{{ dash(users.country_graduation) }}</td>
              </tr>
            </table>
            <table class="rs-grid">
              <thead>
                <tr>
                  <th>#</th><th>ระดับการศึกษา</th><th>วุฒิ/คณะ</th><th>สาขาวิชา</th>
                  <th>สถาบัน</th><th>ปีที่จบ</th><th>ผลการเรียน</th><th>สาขาตามระบบ สอศ.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in educations" :key="'ed' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td>{{ dash(item.education_level) }}</td>
                  <td>{{ dash(item.faculty_name) }}</td>
                  <td>{{ dash(item.branch_name) }}</td>
                  <td>{{ dash(item.academy_name) }}</td>
                  <td class="c">{{ dash(item.year_finish) }}</td>
                  <td class="c">{{ dash(item.academic_results) }}</td>
                  <td>{{ dash(item.name_branch) }}<span v-if="item.name_sub_branch"> / {{ item.name_sub_branch }}</span></td>
                </tr>
                <tr v-if="!educations.length"><td colspan="8" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- ใบอนุญาต -->
            <div class="rs-section">{{ n("licence") }}. ใบอนุญาตประกอบวิชาชีพ</div>
            <table class="rs-grid">
              <thead>
                <tr><th>ประเภท</th><th>เลขที่</th><th>วันที่ออก</th><th>วันหมดอายุ</th><th>สถานะ</th></tr>
              </thead>
              <tbody>
                <tr v-for="lic in licences" :key="lic.label">
                  <td>{{ lic.label }}</td>
                  <td class="c">{{ dash(lic.no) }}</td>
                  <td class="c">{{ thaiDate(lic.on) }}</td>
                  <td class="c">{{ thaiDate(lic.end) }}</td>
                  <td class="c">{{ licenceStatus(lic) }}</td>
                </tr>
                <tr v-if="!licences.length"><td colspan="5" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- ประวัติการรับราชการ -->
            <div class="rs-section">{{ n("history") }}. ประวัติการรับราชการ</div>
            <table class="rs-grid">
              <thead>
                <tr>
                  <th>#</th><th>วันที่</th><th>ถึงวันที่</th><th>ตำแหน่ง</th><th>เลขที่ตำแหน่ง</th>
                  <th>สถานที่ปฏิบัติงาน</th><th>สถานะ</th><th>หน้าที่อื่น</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in workHistories" :key="'wh' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td class="c">{{ thaiDate(item.date_begin) }}</td>
                  <td class="c">{{ thaiDate(item.date_end) }}</td>
                  <td>{{ dash(item.position_s) }}</td>
                  <td class="c">{{ dash(item.id_position_old) }}</td>
                  <td>{{ dash(item.college_name_work) }}</td>
                  <td>{{ String(item.government_status) === "1" ? "ช่วยราชการ" : "-" }}</td>
                  <td>{{ dash(item.special_duty) }}</td>
                </tr>
                <tr v-if="!workHistories.length"><td colspan="8" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- เครื่องราชอิสริยาภรณ์ -->
            <div class="rs-section">{{ n("decoration") }}. ประวัติการได้รับพระราชทานเครื่องราชอิสริยาภรณ์</div>
            <table class="rs-grid">
              <thead>
                <tr>
                  <th>#</th><th>วันที่ได้รับพระราชทาน</th><th>ชื่อเครื่องราชอิสริยาภรณ์</th>
                  <th>ชั้นตรา</th><th>ราชกิจจานุเบกษา</th><th>หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in decorations" :key="'dc' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td class="c">{{ thaiDate(item.received_date) }}</td>
                  <td>{{ dash(item.decoration_name) }}</td>
                  <td>{{ dash(item.decoration_class) }}</td>
                  <td>{{ gazetteText(item) }}</td>
                  <td>{{ dash(item.decoration_note) }}</td>
                </tr>
                <tr v-if="!decorations.length"><td colspan="6" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- การปฏิบัติหน้าที่ -->
            <div class="rs-section">{{ n("duty") }}. การปฏิบัติหน้าที่</div>
            <table class="rs-grid">
              <thead>
                <tr><th>#</th><th>ปี</th><th>สถานศึกษา</th><th>คำสั่งที่</th><th>โครงการ/กิจกรรม</th><th>วันที่</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in duties" :key="'du' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td class="c">{{ dash(item.personnel_dutyYear) }}</td>
                  <td>{{ dash(item.college_name) }}</td>
                  <td>{{ dash(item.personnel_dutyOrderID) }}</td>
                  <td>{{ dash(item.personnel_dutyTopic) }}</td>
                  <td class="c">{{ thaiDate(item.personnel_dutyDateTime) }}</td>
                </tr>
                <tr v-if="!duties.length"><td colspan="6" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- ผลงาน/รางวัล -->
            <div class="rs-section">{{ n("award") }}. ผลงาน/รางวัล/การได้รับการยกย่อง</div>
            <table class="rs-grid">
              <thead>
                <tr><th>#</th><th>ปี</th><th>ผลงาน/รางวัล</th><th>สถานที่</th><th>วันที่</th><th>ระดับ</th><th>ประเภท</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in awards" :key="'aw' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td class="c">{{ dash(item.personnel_awardYear) }}</td>
                  <td>{{ dash(item.performance_name) }}</td>
                  <td>{{ dash(item.performance_location) }}</td>
                  <td class="c">{{ thaiDate(item.performance_time) }}</td>
                  <td>{{ awardLevelLabel(item.award_level) }}</td>
                  <td>{{ awardTypeLabel(item) }}</td>
                </tr>
                <tr v-if="!awards.length"><td colspan="7" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- การพัฒนาตนเอง -->
            <div class="rs-section">{{ n("experience") }}. การพัฒนาตนเองและพัฒนาวิชาชีพ</div>
            <table class="rs-grid">
              <thead>
                <tr><th>#</th><th>ปี</th><th>หลักสูตร/รายการ</th><th>สถานที่</th><th>หน่วยงานที่จัด</th><th>ระยะเวลา</th><th>ชั่วโมง</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in experiences" :key="'ex' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td class="c">{{ dash(item.pexperienceyear_s) }}</td>
                  <td>{{ dash(item.course_name) }}</td>
                  <td>{{ dash(item.course_location) }}</td>
                  <td>{{ dash(item.agency_name) }}</td>
                  <td class="c">{{ dateRange(item.date_time_begin, item.date_time_end) }}</td>
                  <td class="c">{{ dash(item.number_hours) }}</td>
                </tr>
                <tr v-if="experiences.length">
                  <td colspan="6" class="r"><b>รวมจำนวนชั่วโมง</b></td>
                  <td class="c"><b>{{ totalHours }}</b></td>
                </tr>
                <tr v-else><td colspan="7" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- ลูกเสือ -->
            <div class="rs-section">{{ n("scout") }}. ข้อมูลลูกเสือวิสามัญอาชีวศึกษา</div>
            <table class="rs-grid">
              <thead>
                <tr><th>#</th><th>ระดับ</th><th>เหล่า</th><th>ตำแหน่งทางลูกเสือ</th><th>คุณวุฒิ</th><th>วันที่ได้รับคุณวุฒิ</th><th>วิทยากร</th><th>เครื่องหมายเชิดชูเกียรติ</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in scouts" :key="'sc' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td>{{ dash(item.scout_level) }}</td>
                  <td>{{ dash(item.scout_type) }}</td>
                  <td>{{ item.scout_position === "อื่น ๆ" && item.scout_position_other ? item.scout_position_other : dash(item.scout_position) }}</td>
                  <td>{{ item.scout_qualification || "ไม่มีคุณวุฒิ" }}</td>
                  <td class="c">{{ thaiDate(item.woodbadge_date) }}</td>
                  <td class="c">{{ item.scout_instructor || "ไม่เป็น" }}</td>
                  <td>{{ parseList(item.scout_awards).join(", ") || "-" }}</td>
                </tr>
                <tr v-if="!scouts.length"><td colspan="8" class="rs-empty">ไม่มีข้อมูล</td></tr>
              </tbody>
            </table>

            <!-- วินัย -->
            <div class="rs-section">{{ n("discipline") }}. การรักษาวินัย</div>
            <table class="rs-grid">
              <thead>
                <tr><th>#</th><th>วันที่</th><th>หัวข้อ</th><th>รายละเอียด</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in disciplines" :key="'dp' + i">
                  <td class="c">{{ i + 1 }}</td>
                  <td class="c">{{ thaiDate(item.disciplinedate_time) }}</td>
                  <td>{{ dash(item.discipline_blame) }}</td>
                  <td>{{ dash(item.location_disbla) }}</td>
                </tr>
                <tr v-if="!disciplines.length"><td colspan="4" class="rs-empty">ไม่เคยถูกลงโทษทางวินัย</td></tr>
              </tbody>
            </table>

            <table class="rs-sign">
              <tr>
                <td></td>
                <td class="c">
                  <p>ขอรับรองว่าข้อมูลข้างต้นถูกต้องตามความเป็นจริง</p>
                  <p>ลงชื่อ ......................................................</p>
                  <p>( {{ fullName }} )</p>
                  <p>ตำแหน่ง {{ dash(users.position_name) }}</p>
                  <p>วันที่ ........./........................./.............</p>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
const THAI_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];

// ใช้ทั้งตอนแสดงใน dialog (inject ผ่าน <style> ด้านล่าง), ตอนพิมพ์ (iframe) และตอนส่งออก Word
// ให้ผลลัพธ์ทั้ง 3 ทางหน้าตาเหมือนกัน
const RESUME_CSS = `
#resume-print-root, #resume-print-root * { font-family: "TH SarabunIT๙", "TH SarabunPSK", "Sarabun", sans-serif; box-sizing: border-box; }
.rs-page { background: #fff; color: #000; width: 21cm; margin: 0 auto; padding: 1.5cm 1.5cm 1.5cm 2cm; font-size: 15pt; line-height: 1.25; }
.rs-header { width: 100%; border-collapse: collapse; margin-bottom: 8pt; border-bottom: 2px solid #263238; }
.rs-header td { vertical-align: top; padding-bottom: 8pt; }
.rs-title { font-size: 22pt; font-weight: bold; }
.rs-subtitle { font-size: 17pt; font-weight: bold; }
.rs-name { font-size: 19pt; font-weight: bold; margin-top: 4pt; }
.rs-muted { color: #555; font-size: 13pt; }
.rs-photo-cell { width: 3.2cm; text-align: right; }
.rs-photo { width: 3cm; height: 3.8cm; object-fit: cover; border: 1px solid #999; }
.rs-section { font-size: 16pt; font-weight: bold; margin: 12pt 0 4pt; padding: 2pt 6pt; background: #eceff1; border-left: 4px solid #263238; page-break-after: avoid; }
.rs-kv, .rs-grid, .rs-sign { width: 100%; border-collapse: collapse; margin-bottom: 4pt; }
.rs-kv th, .rs-kv td { border: 1px solid #b0bec5; padding: 2pt 5pt; vertical-align: top; text-align: left; }
.rs-kv th { width: 22%; background: #f5f7f8; font-weight: bold; }
.rs-kv td { width: 28%; }
.rs-grid th, .rs-grid td { border: 1px solid #90a4ae; padding: 2pt 4pt; font-size: 13pt; vertical-align: top; }
.rs-grid th { background: #eceff1; text-align: center; font-weight: bold; }
.rs-grid tr { page-break-inside: avoid; }
.rs-empty { text-align: center; color: #777; }
.rs-sign { margin-top: 24pt; page-break-inside: avoid; }
.rs-sign td { width: 50%; }
.rs-sign p { margin: 4pt 0; }
#resume-print-root .c { text-align: center; }
#resume-print-root .r { text-align: right; }
`;

export default {
  name: "ResumeDialog",

  props: {
    value: { type: Boolean, default: false },
    idCard: { type: String, default: "" }
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      today: new Date().toISOString().slice(0, 10),
      users: {},
      educations: [],
      duties: [],
      awards: [],
      experiences: [],
      workHistories: [],
      decorations: [],
      scouts: [],
      disciplines: []
    };
  },

  computed: {
    fullName() {
      const u = this.users;
      return `${u.title_s || ""}${u.frist_name || ""} ${u.last_name || ""}`.trim() || "-";
    },
    isGovEm() {
      return this.users.user_status === "gov_em";
    },
    userTypeLabel() {
      const map = {
        teacher: "ข้าราชการครู",
        director: "ผู้อำนวยการสถานศึกษา",
        se_director: "รองผู้อำนวยการสถานศึกษา",
        gov_em: "พนักงานราชการ",
        "38": "บุคลากรทางการศึกษา มาตรา 38 ค.(2)",
        "3801": "บุคลากรทางการศึกษา มาตรา 38 ค.(2)",
        "3802": "บุคลากรทางการศึกษา มาตรา 38 ค.(2)",
        "3803": "บุคลากรทางการศึกษา มาตรา 38 ค.(2)"
      };
      return map[this.users.user_status] || "ข้าราชการครูและบุคลากรทางการศึกษา";
    },
    maritalLabel() {
      const map = {
        single: "โสด",
        married: "สมรส",
        widow: "หม้าย",
        divorce: "หย่า",
        separate: "แยกกันอยู่"
      };
      return map[this.users.marital_status] || "-";
    },
    hasSpouse() {
      return (
        ["married", "separate"].includes(this.users.marital_status) &&
        !!this.users.frist_namem
      );
    },
    photoUrl() {
      const pic = this.users.personnel_temporary_pic;
      return `${window.location.origin}/HRvecfiles/${pic || "blank-human-image.png"}`;
    },
    birthDate() {
      return this.thaiPartsToIso(this.users.brith_day, this.users.brith_month, this.users.brith_year);
    },
    appointDate() {
      return this.thaiPartsToIso(this.users.appoin_day, this.users.appoin_month, this.users.appoin_year);
    },
    birthDateText() {
      return this.thaiDate(this.birthDate);
    },
    appointDateText() {
      return this.thaiDate(this.appointDate);
    },
    ageText() {
      return this.birthDate ? this.durationText(this.birthDate) : "-";
    },
    serviceAgeText() {
      return this.appointDate ? this.durationText(this.appointDate) : "-";
    },
    salaryText() {
      const s = Number(this.users.salary_s);
      return s ? `${s.toLocaleString("th-TH")} บาท` : "-";
    },
    addressText() {
      const u = this.users;
      const parts = [];
      if (u.pt_homeid) parts.push(`เลขที่ ${u.pt_homeid}`);
      if (u.pt_moo) parts.push(`หมู่ ${u.pt_moo}`);
      if (u.pt_road) parts.push(`ถนน ${u.pt_road}`);
      if (u.address_district_name) parts.push(`ตำบล ${u.address_district_name}`);
      if (u.address_prefecture_name) parts.push(`อำเภอ ${u.address_prefecture_name}`);
      if (u.address_province_name) parts.push(`จังหวัด ${u.address_province_name}`);
      if (u.address_post && u.address_post !== "0") parts.push(u.address_post);
      return parts.join(" ") || "-";
    },
    licences() {
      const u = this.users;
      return [
        { label: "ใบอนุญาตประกอบวิชาชีพครู", no: u.pt_licence, on: u.pt_ondate, end: u.pt_enddate },
        { label: "ใบอนุญาตประกอบวิชาชีพผู้บริหารสถานศึกษา", no: u.pt_licencedr, on: u.pt_ondatedr, end: u.pt_enddatedr },
        { label: "ใบอนุญาตประกอบวิชาชีพศึกษานิเทศก์", no: u.pt_licence_sp, on: u.pt_ondate_sp, end: u.pt_enddate_sp }
      ].filter(l => l.no && String(l.no).trim());
    },
    totalHours() {
      return this.experiences.reduce((sum, e) => sum + (Number(e.number_hours) || 0), 0);
    },
    // เลขหัวข้อ: ข้อ 4 (คู่สมรส) แสดงเฉพาะผู้ที่สมรส เลขหัวข้อถัดไปจึงต้องเลื่อนตาม
    sectionNumbers() {
      const keys = ["education", "licence", "history", "decoration", "duty", "award", "experience", "scout", "discipline"];
      const start = this.hasSpouse ? 5 : 4;
      return keys.reduce((acc, k, i) => ({ ...acc, [k]: start + i }), {});
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open && this.idCard) this.loadAll();
      }
    }
  },

  methods: {
    n(key) {
      return this.sectionNumbers[key];
    },
    close() {
      this.$emit("input", false);
    },

    // แต่ละ endpoint โหลดแยกกันและไม่ล้มทั้งหน้า ถ้าตารางใดยังไม่ migrate/ไม่มีข้อมูล ส่วนนั้นจะแสดง "ไม่มีข้อมูล"
    async fetchList(url, payload) {
      try {
        const res = await this.$http.post(url, { ApiKey: this.ApiKey, ...payload });
        return Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        return [];
      }
    },

    async loadAll() {
      this.loading = true;
      this.today = new Date().toISOString().slice(0, 10);
      const id = this.idCard;
      try {
        const [profile, educations, duties, awards, experiences, workHistories, decorations, scouts, disciplines] =
          await Promise.all([
            this.$http
              .post("personnel_resume.php", { ApiKey: this.ApiKey, id_card: id })
              .then(r => r.data || {})
              .catch(() => ({})),
            this.fetchList("personnel_education.php", { id_card: id }),
            this.fetchList("personnel_duty.php", { personnel_dutyIDCard: id }),
            this.fetchList("personnel_award.php", { personnel_awardid_card: id }),
            this.fetchList("personnel_experience.php", { pexperienceid_card: id }),
            this.fetchList("personnel_work_history.php", { id_card: id }),
            this.fetchList("personnel_decoration.php", { id_card: id }),
            this.fetchList("personnel_scout.php", { id_card: id }),
            this.fetchList("personnel_discipline.php", { disciplineid_card: id })
          ]);
        this.users = profile;
        this.educations = educations;
        this.duties = duties;
        this.awards = awards;
        this.experiences = experiences;
        this.workHistories = this.sortByDate(workHistories, "date_begin");
        this.decorations = this.sortByDate(decorations, "received_date");
        this.scouts = scouts;
        this.disciplines = disciplines;
      } finally {
        this.loading = false;
      }
    },

    sortByDate(list, field) {
      return [...list].sort((a, b) => String(a[field] || "").localeCompare(String(b[field] || "")));
    },

    dash(v) {
      return v === null || v === undefined || String(v).trim() === "" ? "-" : v;
    },

    formatIdCard(id) {
      const s = String(id || "");
      if (s.length !== 13) return this.dash(s);
      return `${s[0]}-${s.slice(1, 5)}-${s.slice(5, 10)}-${s.slice(10, 12)}-${s[12]}`;
    },

    // วัน/เดือน/ปี พ.ศ. แยกคอลัมน์ (brith_*, appoin_*) → "YYYY-MM-DD" ค.ศ.
    thaiPartsToIso(d, m, y) {
      if (!d || !m || !y) return null;
      const year = Number(y) - 543;
      return `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    },

    parseDate(v) {
      if (!v) return null;
      const m = String(v).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (!m || m[1] === "0000") return null;
      let year = Number(m[1]);
      if (year > 2400) year -= 543; // บางคอลัมน์ (varchar) ถูกบันทึกเป็น พ.ศ.
      return new Date(year, Number(m[2]) - 1, Number(m[3]));
    },

    thaiDate(v) {
      const d = this.parseDate(v);
      if (!d) return "-";
      return `${d.getDate()} ${THAI_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;
    },

    dateRange(a, b) {
      if (!a && !b) return "-";
      if (!b || a === b) return this.thaiDate(a);
      return `${this.thaiDate(a)} - ${this.thaiDate(b)}`;
    },

    durationText(from) {
      const start = this.parseDate(from);
      if (!start) return "-";
      const now = new Date();
      let y = now.getFullYear() - start.getFullYear();
      let m = now.getMonth() - start.getMonth();
      let d = now.getDate() - start.getDate();
      if (d < 0) {
        m -= 1;
        d += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      }
      if (m < 0) {
        y -= 1;
        m += 12;
      }
      return `${y} ปี ${m} เดือน ${d} วัน`;
    },

    licenceStatus(lic) {
      const end = this.parseDate(lic.end);
      if (!end) return "-";
      return end >= new Date() ? "ยังไม่หมดอายุ" : "หมดอายุแล้ว";
    },

    gazetteText(item) {
      const parts = [];
      if (item.gazette_book) parts.push(`เล่ม ${item.gazette_book}`);
      if (item.gazette_issue) parts.push(`ตอน ${item.gazette_issue}`);
      if (item.gazette_page) parts.push(`หน้า ${item.gazette_page}`);
      if (item.gazette_date) parts.push(`ลงวันที่ ${this.thaiDate(item.gazette_date)}`);
      return parts.join(" ") || "-";
    },

    awardLevelLabel(level) {
      const map = {
        college: "ระดับวิทยาลัย",
        provincial: "ระดับสำนักงานอาชีวศึกษาจังหวัด",
        sector: "ระดับภาค",
        vocational: "ระดับอาชีวศึกษา",
        nation: "ระดับชาติ",
        word: "ระดับโลก"
      };
      return map[level] || this.dash(level);
    },

    awardTypeLabel(item) {
      const map = {
        skill: "การแข่งขันทักษะ",
        innovation: "การประกวดสิ่งประดิษฐ์",
        award: "รางวัลเชิดชูเกียรติ"
      };
      if (item.type_performance === "etc") {
        return item.personnel_awardEtc ? `อื่นๆ (${item.personnel_awardEtc})` : "อื่นๆ";
      }
      return map[item.type_performance] || this.dash(item.type_performance);
    },

    parseList(val) {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [String(parsed)];
      } catch (e) {
        return String(val).split(",").map(s => s.trim()).filter(Boolean);
      }
    },

    buildHtmlDocument() {
      const body = this.$refs.printRoot ? this.$refs.printRoot.outerHTML : "";
      return `<!doctype html><html><head><meta charset="utf-8"><title>ประวัติ ${this.fullName}</title>
<style>@page { size: A4; margin: 0; } body { margin: 0; }
@media print { .rs-page { width: auto; padding: 1cm 1cm 1cm 1.5cm; } }
${RESUME_CSS}</style></head><body>${body}</body></html>`;
    },

    // พิมพ์ผ่าน iframe ที่ซ่อนอยู่ — ไม่ต้องเปิดแท็บใหม่ และไม่ติด layout/เมนูของแอปเหมือนการสั่ง window.print() ตรงๆ
    printResume() {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(this.buildHtmlDocument());
      doc.close();

      const doPrint = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      };
      // รอรูปถ่ายโหลดเสร็จก่อนสั่งพิมพ์ ไม่เช่นนั้นรูปจะหายจากหน้ากระดาษ
      const img = doc.querySelector("img");
      if (img && !img.complete) {
        img.onload = img.onerror = doPrint;
      } else {
        doPrint();
      }
    },

    exportWord() {
      const html =
        "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>" +
        `<head><meta charset='utf-8'><style>${RESUME_CSS}</style></head><body>` +
        (this.$refs.printRoot ? this.$refs.printRoot.outerHTML : "") +
        "</body></html>";
      const blob = new Blob(["﻿", html], { type: "application/msword" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ประวัติ_${this.users.id_card || "personnel"}.doc`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  },

  mounted() {
    // ใส่ CSS ชุดเดียวกับที่ใช้พิมพ์/ส่งออก Word เข้า document หลักครั้งเดียว เพื่อให้หน้าจอแสดงผลตรงกับกระดาษ
    if (!document.getElementById("resume-dialog-css")) {
      const style = document.createElement("style");
      style.id = "resume-dialog-css";
      style.textContent = RESUME_CSS;
      document.head.appendChild(style);
    }
  }
};
</script>

<style scoped>
.resume-viewport {
  background: #cfd8dc;
  min-height: calc(100vh - 48px);
  padding: 24px 12px;
  overflow-x: auto;
}
.resume-viewport >>> .rs-page {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}
</style>
